import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  /** Canonical path (e.g. "/buscar"). Falls back to current pathname. */
  canonical?: string;
  /** OpenGraph type. Defaults to "website". Use "article" for blog posts, "product" for listings. */
  type?: "website" | "article" | "product" | "profile";
  /** Absolute URL or relative path to the share image (1200x630 recommended). */
  image?: string;
  /** Optional JSON-LD structured data. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Whether the page should be indexed. Defaults to true. */
  noindex?: boolean;
}

 import { SITE_CONFIG } from "@/config/site-meta";
 const SITE_URL = SITE_CONFIG.url;
 const DEFAULT_IMAGE = SITE_CONFIG.logo;

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
};

export const useSEO = ({
  title,
  description,
  canonical,
  type = "website",
  image,
  jsonLd,
  noindex = false,
}: SEOProps) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const path = canonical ?? (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalUrl = path.startsWith("http") ? path : `${SITE_URL}${path}`;
    const imageUrl = image
      ? image.startsWith("http")
        ? image
        : `${SITE_URL}${image}`
      : DEFAULT_IMAGE;

    // Standard meta
    const desc = upsertMeta('meta[name="description"]', "name", "description", description);
    const robots = upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );

    // Canonical
    const canonicalEl = upsertLink("canonical", canonicalUrl);

    // OpenGraph
    const ogTitle = upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    const ogDesc = upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    const ogType = upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    const ogUrl = upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    const ogImage = upsertMeta('meta[property="og:image"]', "property", "og:image", imageUrl);
     const ogImageAlt = upsertMeta('meta[property="og:image:alt"]', "property", "og:image:alt", title);
     const ogSiteName = upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_CONFIG.name);

    // Twitter
    const twCard = upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    const twTitle = upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    const twDesc = upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    const twImage = upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", imageUrl);

    // JSON-LD
    let scriptEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute("data-dynamic-seo", "true");
      scriptEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = prevTitle;
      if (scriptEl) scriptEl.remove();
      // Note: we intentionally leave meta tags in place — next page's useSEO overwrites them.
      void [desc, robots, canonicalEl, ogTitle, ogDesc, ogType, ogUrl, ogImage, ogImageAlt, twCard, twTitle, twDesc, twImage];
    };
  }, [title, description, canonical, type, image, jsonLd, noindex]);
};
